import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { V1Scheduler } from 'takeorder-groupbuying-scheduler-js';
import {
  SCHEDULE_TYPES,
  WEEKDAYS,
} from '@/utils/constants';

const RECEIVE_PERIOD_MAX_DAY_DIFF = 365;
export default {
  name: 'SchedulePeriodSetting',
  components: {
  },
  props: {
    createMode: {
      default: true,
      type: Boolean,
    },
    viewMode: {
      default: false,
      type: Boolean,
    },
    value: {
      default: () => ({}),
      type: Object,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      // NOTE: 公告時間
      orderCreate: {
        day: 0,
        time: null,
      },
      // NOTE: 開放訂購時間範圍
      orderPeriod: {
        endDay: 0,
        endTime: null,
        startDay: 0,
        startTime: null,
        startAtSameTime: null,
        endAtSameTime: null,
      },
      // NOTE: 領取週期、週期範圍、領取時段
      receivePeriod: {
        endDate: null,
        monthDays: [],
        startDate: null,
        sections: [],
        type: null,
        weekdays: [],
      },
    };
  },
  computed: {
    monthDayOptions() {
      return Array.from(
        { length: 32 },
        (_, i) => {
          const key = (i + 1) % 32;
          return { label: key ? `${key}號` : '最後一天', value: key };
        },
      );
    },
    orderCreateDayOptions() {
      return Array.from(
        { length: 8 },
        (_, i) => ({ label: i ? `前 ${i} 天` : '當下', value: i }),
      );
    },
    orderEndDayOptions() {
      const arr = [{ label: '指定日期', value: -1, disabled: this.receiveDayOverMonth || this.orderPeriod.startDay !== -1 }];
      arr.push(...Array.from(
        { length: 31 },
        (_, i) => ({ label: i ? `領取日前${i}天` : '領取日當天', value: i, disabled: false }),
      ));
      return arr;
    },
    orderStartDayOptions() {
      const arr = [{ label: '指定日期', value: -1, disabled: this.receiveDayOverMonth }];
      arr.push(...Array.from(
        { length: 31 },
        (_, i) => ({ label: i ? `領取日前${i}天` : '領取日當天', value: i, disabled: false }),
      ));
      return arr;
    },
    receiveSectionEndDayOptions() {
      return Array.from(
        { length: 7 },
        (_, i) => ({ label: i ? `第${i + 1}天` : '當天', value: i }),
      );
    },
    SCHEDULE_TYPES() {
      return SCHEDULE_TYPES;
    },
    weekdayOptions() {
      return Array.from(
        { length: WEEKDAYS.length },
        (_, i) => {
          const key = (i + 1) % 7;
          return { label: WEEKDAYS[key], value: key };
        },
      );
    },
    receiveDayOverMonth() {
      return dayjs(this.receivePeriod.endDate).diff(this.receivePeriod.startDate, 'day') > 31;
    },
    orderPeriodError() {
      return this.v$.orderPeriod.endTime.$error
        || this.v$.orderPeriod.endAtSameTime.$error
        || this.v$.orderPeriod.startAtSameTime.$error;
    },
    orderPeriodErrorMessage() {
      return this.v$.orderPeriod.endTime.$errors[0]?.$message
        || this.v$.orderPeriod.endAtSameTime.$errors[0]?.$message
        || this.v$.orderPeriod.startAtSameTime.$errors[0]?.$message;
    },
    estimatedSchedule() {
      const settingsCopy = cloneDeep(this.createSettings());
      // clear interferences
      settingsCopy.endBeforeReceiveDay = 1;
      settingsCopy.orderStart = 0;
      settingsCopy.orderPeriodDay = 0;
      settingsCopy.orderPeriodTime = ['00:00', '01:00'];
      settingsCopy.startAtSameTime = null;
      settingsCopy.endAtSameTime = null;

      const list = V1Scheduler.getAllSchedule(settingsCopy);
      return list;
    },
    estimatedFirstReceiveStartTime() {
      const list = this.estimatedSchedule;
      const firstScheduledTime = list?.[0]?.groupBuyingReceiveTimes?.[0]?.[0];

      if (firstScheduledTime) {
        return dayjs(firstScheduledTime);
      }

      const { startTime } = this.receivePeriod.sections[0];
      return dayjs(this.receivePeriod.startDate)
        .hour(startTime.getHours())
        .minute(startTime.getMinutes());
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    createReceiveSection() {
      const last = this.receivePeriod.sections.at(-1);
      if (!last) {
        return {
          startDay: 0,
          startTime: dayjs().hour(12).minute(0).toDate(),
          endDay: 0,
          endTime: dayjs().hour(14).minute(0).toDate(),
        };
      }

      const lastStart = this.mergeDayTimeAdd(last.startDay, last.startTime);
      const lastEnd = this.mergeDayTimeAdd(last.endDay, last.endTime);
      const newEnd = lastEnd.add(2, 'hour');
      const maxDayDiff = 6;
      const diff = newEnd.clone().startOf('day').diff(dayjs().startOf('day'), 'day');
      const startTime = lastEnd.isBefore(lastStart) || lastEnd.isAfter(dayjs().endOf('day'))
        ? dayjs(last.startTime).toDate()
        : dayjs().hour(lastEnd.hour()).minute(lastEnd.minute()).toDate();
      const endDay = diff > maxDayDiff ? maxDayDiff : diff;
      const endTime = diff > maxDayDiff
        ? dayjs().endOf('day').toDate()
        : dayjs().hour(newEnd.hour()).minute(newEnd.minute()).toDate();

      return {
        startDay: 0,
        startTime,
        endDay,
        endTime,
      };
    },
    createSettings() {
      const settings = {
        groupBuyingStartTime:
          dayjs((dayjs(this.orderPeriod.groupBuyingStartTime).format('YYYY-MM-DD HH:mm:00'))).format(),
        groupBuyingReceiveTimes: this.receivePeriod.sections.map((section) => {
          const start = this.mergeDayTimeAdd(
            section.startDay,
            section.startTime,
          );
          const end = this.mergeDayTimeAdd(
            section.endDay,
            section.endTime,
          );
          return [
            dayjs(start).format('YYYY-MM-DDTHH:mm:ss'),
            dayjs(end).format('YYYY-MM-DDTHH:mm:ss'),
          ];
        }),
        firstReceiveDate:
          dayjs(this.receivePeriod.startDate).startOf('day').format(),
        lastReceiveDate:
          dayjs(this.receivePeriod.endDate).startOf('day').format(),
        monthDays: this.receivePeriod.monthDays,
        orderCreateDay: this.orderCreate.day,
        orderCreateTime: dayjs(this.orderCreate.time).format('HH:mm'),
        orderPeriodDay: this.orderPeriod.startDay - this.orderPeriod.endDay,
        orderPeriodTime: [
          dayjs(this.orderPeriod.startTime).format('HH:mm'),
          dayjs(this.orderPeriod.endTime).format('HH:mm'),
        ],
        startAtSameTime:
          this.orderPeriod.startAtSameTime
            ? dayjs(this.orderPeriod.startAtSameTime).startOf('day').format()
            : null,
        endAtSameTime:
          this.orderPeriod.endAtSameTime
            ? dayjs(this.orderPeriod.endAtSameTime).startOf('day').format()
            : null,
        endBeforeReceiveDay: this.orderPeriod.endDay === -1 ? null : this.orderPeriod.endDay,
        orderStart: this.orderPeriod.startDay,
        type: this.receivePeriod.type,
        period: this.receivePeriod.period,
        weekday: this.receivePeriod.weekdays,
        version: 2,
      };
      return settings;
    },
    init() {
      const {
        groupBuyingReceiveTimes = [],
        groupBuyingStartTime = dayjs((dayjs().format('YYYY-MM-DD HH:mm:00'))).format(),
        firstReceiveDate: receiveStartDate = dayjs(),
        lastReceiveDate: receiveEndDate,
        monthDays = [],
        endBeforeReceiveDay = 0,
        orderCreateDay = 0,
        orderCreateTime,
        orderPeriodDay = 0,
        orderPeriodTime: [
          orderPeriodTimeStart,
          orderPeriodTimeEnd,
        ] = ['10:00', '12:00'],
        orderStart = 0,
        period = 1,
        startAtSameTime,
        endAtSameTime,
        type = SCHEDULE_TYPES.PERIOD,
        weekday = [],
      } = this.value || {};

      this.orderCreate.day = orderCreateDay;
      // NOTE: old version settings do not have orderCreateTime, use orderPeriodTime.start instead
      this.orderCreate.time = this.timeToDayjs(
        orderCreateTime || orderPeriodTimeStart,
      ).toDate();

      this.orderPeriod.startTime = this.timeToDayjs(orderPeriodTimeStart).toDate();
      this.orderPeriod.endTime = this.timeToDayjs(orderPeriodTimeEnd).toDate();
      this.orderPeriod.groupBuyingStartTime = groupBuyingStartTime;

      this.receivePeriod.type = type;
      this.receivePeriod.period = period;
      this.receivePeriod.monthDays = monthDays;
      this.receivePeriod.weekdays = weekday;
      this.receivePeriod.startDate = dayjs(receiveStartDate).toDate();
      this.receivePeriod.endDate = dayjs(
        receiveEndDate || dayjs(receiveStartDate).add(1, 'month'),
      ).toDate();
      this.receivePeriod.sections = groupBuyingReceiveTimes.length
        ? groupBuyingReceiveTimes.map((times) => this.toReceiveSection(times))
        : [this.createReceiveSection()];
      this.orderPeriod.startAtSameTime = startAtSameTime
        ? dayjs(startAtSameTime).toDate() : null;
      this.orderPeriod.endAtSameTime = endAtSameTime
        ? dayjs(endAtSameTime).toDate() : null;
      this.orderPeriod.startDay = this.orderPeriod.startAtSameTime ? -1 : orderStart;
      if (this.orderPeriod.endAtSameTime) {
        this.orderPeriod.endDay = -1;
      } else if (this.orderPeriod.startAtSameTime) {
        this.orderPeriod.endDay = endBeforeReceiveDay;
      } else {
        this.orderPeriod.endDay = orderStart - orderPeriodDay;
      }
      this.onEdit(null, true);
    },
    disableReceivePeriodEndDate(date) {
      if (dayjs(date).isBefore(dayjs(), 'day')) {
        return true;
      }
      if (!this.receivePeriod.startDate) {
        return false;
      }
      const diff = dayjs(date).diff(this.receivePeriod.startDate, 'day');
      return diff < 0 || diff > RECEIVE_PERIOD_MAX_DAY_DIFF;
    },
    disableReceivePeriodStartDate(date) {
      const limit = dayjs();
      if (dayjs(date).isBefore(limit, 'day')) {
        return true;
      }
      if (!this.receivePeriod.endDate) {
        return false;
      }
      const diff = dayjs(date).diff(this.receivePeriod.endDate, 'day');
      return diff > 0 || diff < -RECEIVE_PERIOD_MAX_DAY_DIFF;
    },
    disableStartAtSameTimeDate(date) {
      if (dayjs(date).isAfter(dayjs(this.estimatedFirstReceiveStartTime), 'day')) {
        return true;
      }
      if (dayjs(date).isBefore(dayjs(), 'day')) {
        return true;
      }
      return false;
    },
    disableEndAtSameTimeDate(date) {
      if (dayjs(date).isAfter(dayjs(this.estimatedFirstReceiveStartTime), 'day')) {
        return true;
      }
      if (dayjs(date).isBefore(dayjs(), 'day')) {
        return true;
      }
      return false;
    },
    formatDate(date) {
      return dayjs(date).format('YYYY/MM/DD');
    },
    formatTime(time) {
      return dayjs(time).format('A hh:mm');
    },
    mergeDayTimeAdd(day, time, base = dayjs()) {
      return base.add(day, 'day')
        .hour(time.getHours())
        .minute(time.getMinutes());
    },
    mergeDayTimeSubtract(day, time, base = dayjs()) {
      return base.subtract(day, 'day')
        .hour(time.getHours())
        .minute(time.getMinutes());
    },
    onAddReceiveSection() {
      this.receivePeriod.sections.push(this.createReceiveSection());
    },
    onDeleteReceiveSection(index) {
      this.receivePeriod.sections.splice(index, 1);
    },
    onEdit(_, isInit = false) {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      const settings = this.createSettings();

      if (isInit) {
        this.$emit('init', settings);
      } else {
        this.$emit('input', settings);
      }
    },
    onTypeChange(val) {
      if (val === SCHEDULE_TYPES.PERIOD) {
        this.receivePeriod.monthDays = [];
        this.receivePeriod.weekdays = [];
      } else if (val === SCHEDULE_TYPES.WEEKLY || val === SCHEDULE_TYPES.DOUBLE_WEEKLY) {
        this.receivePeriod.monthDays = [];
      } else if (val === SCHEDULE_TYPES.MONTHLY) {
        this.receivePeriod.weekdays = [];
      }
    },
    timeToDayjs(time) {
      const [hour, minute] = time.split(':');
      return dayjs().hour(hour).minute(minute);
    },
    toReceiveSection(times) {
      const [start, end] = times;
      const diff = dayjs(end).startOf('day')
        .diff(dayjs(start).startOf('day'), 'day');
      return {
        startDay: 0,
        startTime: this.timeToDayjs(dayjs(start).format('HH:mm')).toDate(),
        endDay: diff,
        endTime: this.timeToDayjs(dayjs(end).format('HH:mm')).toDate(),
      };
    },
    onStartDayChange(newVal) {
      if (newVal === -1) {
        this.orderPeriod.startAtSameTime = this.value.startAtSameTime
          ? dayjs(this.value.startAtSameTime).toDate() : dayjs().add(1, 'day').toDate();
      } else {
        this.orderPeriod.startAtSameTime = null;
        if (this.orderPeriod.endDay === -1) {
          this.orderPeriod.endDay = 0;
        }
      }
    },
    onEndDayChange(newVal) {
      if (newVal !== -1) {
        this.orderPeriod.endAtSameTime = null;
      } else {
        this.orderPeriod.endAtSameTime = this.value.endAtSameTime
          ? dayjs(this.orderPeriod.endAtSameTime).toDate() : this.orderPeriod.startAtSameTime;
      }
    },
  },
  watch: {
    'v$.$error': {
      handler(val) {
        this.$emit('error', val);
      },
    },
    receiveDayOverMonth: {
      handler(newVal) {
        if (newVal) {
          if (this.orderPeriod.endDay === -1) {
            this.orderPeriod.endDay = 0;
            this.onEndDayChange(0);
          }
          if (this.orderPeriod.startDay === -1) {
            this.orderPeriod.startDay = 0;
            this.onStartDayChange(0);
          }
          this.onEdit();
        }
      },
    },
  },
  validations() {
    return {
      orderCreate: {
        isSameOrBeforeOrderStart: helpers.withMessage(
          '公告時間不能大於訂購開始時間',
          () => {
            const orderStart = dayjs(this.orderPeriod.startTime);
            const createDate = this.mergeDayTimeSubtract(
              this.orderCreate.day,
              this.orderCreate.time,
              orderStart.clone(),
            );
            return createDate.isSameOrBefore(orderStart);
          },
        ),
      },
      orderPeriod: {
        endTime: {
          isSameOrBeforeReceive: helpers.withMessage(
            '訂購結束時間不能大於領取開始時間',
            () => {
              const receiveStart = this.estimatedFirstReceiveStartTime;
              const anchor = this.orderPeriod.endDay === -1
                ? this.orderPeriod.endAtSameTime
                : receiveStart.clone().subtract(this.orderPeriod.endDay, 'day');

              const orderEnd = dayjs(anchor)
                .hour(this.orderPeriod.endTime.getHours())
                .minute(this.orderPeriod.endTime.getMinutes());

              return orderEnd.isSameOrBefore(receiveStart, 'minute');
            },
          ),
          isSameOrAfterOrderStart: helpers.withMessage(
            '訂購結束時間不能小於訂購開始時間',
            () => {
              const receiveStart = this.estimatedFirstReceiveStartTime;

              const orderStart = this.orderPeriod.startDay === -1
                ? dayjs(this.orderPeriod.startAtSameTime)
                  .hour(this.orderPeriod.startTime.getHours())
                  .minute(this.orderPeriod.startTime.getMinutes())
                : this.mergeDayTimeSubtract(
                  this.orderPeriod.startDay,
                  this.orderPeriod.startTime,
                  receiveStart.clone(),
                );

              const orderEnd = this.orderPeriod.endDay === -1
                ? dayjs(this.orderPeriod.endAtSameTime)
                  .hour(this.orderPeriod.endTime.getHours())
                  .minute(this.orderPeriod.endTime.getMinutes())
                : this.mergeDayTimeSubtract(
                  this.orderPeriod.endDay,
                  this.orderPeriod.endTime,
                  receiveStart.clone(),
                );

              return orderEnd.isSameOrAfter(orderStart, 'minute');
            },
          ),
        },
        endAtSameTime: {
          isSameOrAfterOrderStartCustom: helpers.withMessage(
            '訂購結束時間不能小於訂購開始時間',
            () => {
              if (this.orderPeriod.endDay !== -1) {
                return true;
              }
              const end = dayjs(this.orderPeriod.endAtSameTime)
                .hour(this.orderPeriod.endTime.getHours())
                .minute(this.orderPeriod.endTime.getMinutes());
              const start = dayjs(this.orderPeriod.startAtSameTime)
                .hour(this.orderPeriod.startTime.getHours())
                .minute(this.orderPeriod.startTime.getMinutes());
              return end.isSameOrAfter(start, 'minute');
            },
          ),
        },
        startAtSameTime: {
          isSameOrAfterReceive: helpers.withMessage(
            '訂購開始時間不能大於領取開始時間',
            () => {
              if (this.orderPeriod.startDay !== -1) {
                return true;
              }

              const receiveStart = this.estimatedFirstReceiveStartTime;

              const start = dayjs(this.orderPeriod.startAtSameTime)
                .hour(this.orderPeriod.startTime.getHours())
                .minute(this.orderPeriod.startTime.getMinutes());

              return start.isSameOrBefore(receiveStart, 'minute');
            },
          ),
        },
      },
      receivePeriod: {
        monthDays: {
          required: helpers.withMessage('請選擇日期', (val) => {
            if (this.receivePeriod.type !== SCHEDULE_TYPES.MONTHLY) {
              return true;
            }
            return !!val.length;
          }),
        },
        sections: {
          $each: helpers.forEach({
            startTime: {
              isSameOrAfterPreviousStart: helpers.withMessage(
                '領取開始時間不能小於上一個領取開始時間',
                (val, current, index) => {
                  const previous = this.receivePeriod.sections[index - 1];
                  if (!previous) {
                    return true;
                  }
                  return dayjs(current.startTime)
                    .isSameOrAfter(dayjs(previous.startTime), 'minute');
                },
              ),
            },
            endTime: {
              isSameOrAfterStart: helpers.withMessage(
                '領取結束時間不能小於領取開始時間',
                (val, current) => {
                  const start = this.mergeDayTimeSubtract(current.startDay, current.startTime);
                  const end = this.mergeDayTimeAdd(current.endDay, current.endTime);
                  return end.isSameOrAfter(start, 'minute');
                },
              ),
            },
          }),
        },
        weekdays: {
          required: helpers.withMessage('請選擇星期', (val) => {
            if (!(this.receivePeriod.type === SCHEDULE_TYPES.WEEKLY
              || this.receivePeriod.type === SCHEDULE_TYPES.DOUBLE_WEEKLY)) {
              return true;
            }
            return !!val.length;
          }),
        },
      },
    };
  },
};
